import { gzip, ungzip, inflate } from 'pako';
import { axiosClient } from '@/lib/axiosClient';
import { buildApiBase } from '@/lib/buildApiBase';
import { sha256 } from './sha256';
import type { Script, LuggageData, TransportObject } from './types';
import { REQUEST_TYPE } from './types';

// ============ API BASE URLS ============
// Matches AngularJS globalService.getAPIName() patterns

const SPIGLASSAPI_BASE = buildApiBase('spiglassapi');
const SPIGLASSGOAPI_BASE = buildApiBase('spiglassgoapi');
const VMAPI_BASE = buildApiBase('vmapi');

// ============ ORDER IMAGES ============

/** One order image from the CoreScript images endpoint (loose — extra fields ignored). */
export interface OrderImage {
  guid?: string;
  /** Full-size / thumbnail image URL. */
  webFileName?: string;
  /** Image caption (the images endpoint returns `description`). */
  description?: string;
  /** Category prefix, e.g. "Document" for non-photo files (PDFs/invoices). */
  prefix?: string;
  /** Original file name (SafeView ImageDialog: `imageFileName`). */
  imageFileName?: string;
  /** Uploaded date (SafeView ImageDialog: `dateCreated`). */
  dateCreated?: string | number;
  /** Image-taken date (SafeView ImageDialog: `imageTakenDate`). */
  imageTakenDate?: string | number;
  descText?: string;
  category?: string;
  scanDate?: string;
  [key: string]: unknown;
}

// CoreScript (audit-results) API base + context/key, shared by order media and
// the task-library script fetch below.
const CORESCRIPT_API_BASE = buildApiBase('corescriptapi');
const AUDIT_CONTEXT = 'AuditResults';
const AUDIT_KEY = 'WorkOrder.OrderNumber';

/**
 * Order images for the gallery:
 *   GET {corescriptapi}/images/AuditResults/WorkOrder.OrderNumber/{orderNumber}
 * Each image carries a `guid` (used for MediaGroup) and `webFileName` (thumbnail).
 */
export async function getOrderImages(orderNumber: string | number): Promise<OrderImage[]> {
  const response = await axiosClient.get(
    `${CORESCRIPT_API_BASE}/images/${AUDIT_CONTEXT}/${AUDIT_KEY}/${orderNumber}`
  );
  return normalizeImages(response.data);
}

/** The images endpoint may return an array, { images: [...] }, or an object map. */
function normalizeImages(data: unknown): OrderImage[] {
  if (Array.isArray(data)) return data as OrderImage[];
  if (data && typeof data === 'object') {
    const inner = (data as { images?: unknown }).images;
    if (Array.isArray(inner)) return inner as OrderImage[];
    if (inner && typeof inner === 'object') return Object.values(inner) as OrderImage[];
    // A bare object map of images keyed by id.
    return Object.values(data as Record<string, unknown>).filter(
      (v): v is OrderImage => !!v && typeof v === 'object'
    );
  }
  return [];
}

/** A server-side image copy (new GUID) returned by the clone endpoint. */
interface CloneMediaResponse {
  media?: { imageGuid: string; webFileName: string }[];
}

/** A cloned photo: a brand-new GUID pointing at a server-side image copy. */
export interface ClonedMedia {
  guid: string;
  webFileName: string;
}

/**
 * Server-side duplicate photos onto a label — each copy gets a NEW GUID
 * (ports SafeView's ImagesApi.cloneImage). Returns the new {guid, webFileName}
 * pairs so the caller can attach them.
 *   PUT {corescriptapi}/clonemedia/AuditResults/WorkOrder.OrderNumber/{orderNumber}
 *   body: { media: { label, guids } }  ->  { media: [{ imageGuid, webFileName }] }
 */
export async function cloneMedia(
  orderNumber: string | number,
  label: string,
  guids: string[]
): Promise<ClonedMedia[]> {
  const response = await axiosClient.put<CloneMediaResponse>(
    `${CORESCRIPT_API_BASE}/clonemedia/${AUDIT_CONTEXT}/${AUDIT_KEY}/${orderNumber}`,
    { media: { label, guids } }
  );
  return (response.data?.media ?? []).map((m) => ({
    guid: m.imageGuid,
    webFileName: m.webFileName,
  }));
}

interface ScriptHashResponse {
  hash: string;
  hashes?: Record<string, string>;
}

interface GetScriptResponse {
  scriptHash?: string;
  scriptValueGzip?: string;
  labelsGzip?: string;
}

/**
 * GET {corescriptapi}/scripthash/AuditResults/WorkOrder.OrderNumber/{orderNumber}
 * -> { hash, hashes: { TaskLibrary, ... } }
 */
export async function getScriptHashes(orderNumber: string | number): Promise<ScriptHashResponse> {
  const response = await axiosClient.get<ScriptHashResponse>(
    `${CORESCRIPT_API_BASE}/scripthash/${AUDIT_CONTEXT}/${AUDIT_KEY}/${orderNumber}`
  );
  return response.data;
}

/** GET {corescriptapi}/GetScript/{hash} -> { scriptValueGzip, labelsGzip } */
export async function getScriptByHash(hash: string): Promise<GetScriptResponse> {
  const response = await axiosClient.get<GetScriptResponse>(
    `${CORESCRIPT_API_BASE}/GetScript/${hash}`
  );
  return response.data;
}

/** base64 gzip -> inflated JSON (ports SafeView's decodeUnzip, Buffer-free). */
export function decodeScript(base64Gzip: string): unknown {
  const binary = atob(base64Gzip);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const text = inflate(bytes, { to: 'string' }).replaceAll(', ', ',');
  return JSON.parse(text);
}

/**
 * Luggage JSON for the order (CoreScript audit context):
 *   GET {corescriptapi}/luggage/AuditResults/WorkOrder.OrderNumber/{orderNumber}
 * Returns a JSON string ('{}' on failure). Distinct from `getLuggage` below,
 * which is the spiglassgoapi PropertyLoanLite luggage.
 */
export async function fetchLuggageJson(orderNumber: string | number): Promise<string> {
  try {
    const response = await axiosClient.get(
      `${CORESCRIPT_API_BASE}/luggage/${AUDIT_CONTEXT}/${AUDIT_KEY}/${orderNumber}`
    );
    return JSON.stringify(response.data ?? {});
  } catch {
    return '{}';
  }
}

// ============ LUGGAGE ============

/**
 * Fetch luggage (property context data).
 * AngularJS: generalDataService.getLuggage(propertyLoanId)
 * Endpoint: GET spiglassgoapi/API/1.1/PropertyLoanLite/SG/{propertyLoanId}
 */
export async function getLuggage(propertyLoanId: string | number): Promise<LuggageData> {
  const response = await axiosClient.get(
    `${SPIGLASSGOAPI_BASE}/API/1.1/PropertyLoanLite/SG/${propertyLoanId}`
  );
  // AngularJS: $http.get() returns full response, then corescript.component.js extracts .data.questions
  // Axios already unwraps .data, so we extract .questions here
  const data = response.data;
  if (data && data.questions) {
    return data.questions;
  }
  return data;
}

/**
 * Fetch work order luggage (seed answers).
 * AngularJS: generalDataService.getWorkOrderLuggage(propertyLoanId, orderNumber)
 * Endpoint: GET spiglassgoapi/API/1.1/PropertyLoanLite/SG/{propertyLoanId}/{orderNumber}
 * Returns: response.data.questions (only the questions portion)
 */
export async function getWorkOrderLuggage(
  propertyLoanId: string | number,
  orderNumber: string | number
): Promise<LuggageData> {
  const response = await axiosClient.get(
    `${SPIGLASSGOAPI_BASE}/API/1.1/PropertyLoanLite/SG/${propertyLoanId}/${orderNumber}`
  );
  return response.data.questions;
}

// ============ SCRIPT ============

/**
 * Fetch a core script definition.
 * AngularJS: generalDataService.getCoreScript(name, orderNumber, forworkcode, sourceSystem)
 *
 * Two endpoints:
 *   forworkcode=true:  GET spiglassapi/API/Script/GetScriptByWorkCode/?orderNumber=...&sourceSystem=...&name=...
 *   forworkcode=false: GET spiglassapi/API/Script/GetScript/?sourceSystem=...&name=...
 *
 * Response: { body: "<JSON string>" }
 * Parses body JSON, adds SHA256 hash as script.version
 */
export async function getCoreScript(
  name: string,
  orderNumber?: string | number,
  forworkcode?: boolean,
  sourceSystem = 'SPIGlassAudit'
): Promise<Script | null> {
  const endpoint = forworkcode
    ? `${SPIGLASSAPI_BASE}/API/Script/GetScriptByWorkCode/`
    : `${SPIGLASSAPI_BASE}/API/Script/GetScript/`;

  const params: Record<string, string | number | undefined> = {
    sourceSystem,
    name,
  };

  if (forworkcode && orderNumber !== undefined) {
    params.orderNumber = orderNumber;
  }

  const response = await axiosClient.get<{ body: string }>(endpoint, { params });

  if (!response.data) return null;

  const script: Script = JSON.parse(response.data.body);
  script.version = sha256(response.data.body);
  return script;
}

// ============ LANGUAGE PACK ============

/**
 * Fetch the language pack from the API, lowercase the keys, gzip-store it
 * into localStorage, and return the dict.
 *
 * Mirrors AngularJS public/index.js lines 492-502, which only runs for SGStaff
 * users. Non-SGStaff users skip the load, so localStorage.languagePack is
 * empty — that is what produces the "raw script keys" UI bug here.
 *
 * We always fetch on demand so the React form works for every user.
 */
async function fetchAndStoreLanguagePack(): Promise<Record<string, string>> {
  try {
    const response = await axiosClient.get<{ keyValues?: Record<string, string> }>(
      `${SPIGLASSAPI_BASE}/API/Script/GetLanguagePack`,
      { params: { language: 'EN' } }
    );
    const keyValues = response.data?.keyValues || {};

    // AngularJS lowercases keys before storing (public/index.js:497-498) and
    // `translate()` lowercases the lookup key (coreScriptEngine.ts:139).
    const translations: Record<string, string> = {};
    for (const property in keyValues) {
      translations[property.toLowerCase()] = keyValues[property];
    }

    // Populate the in-memory cache AND persist the gzipped pack to localStorage
    // (via setLanguagePack → persistLanguagePack) so subsequent page reloads
    // read it synchronously without re-fetching or blocking render.
    setLanguagePack(translations);

    return translations;
  } catch (err) {
    console.error('[loadLanguagePack] failed to fetch language pack', err);
    return {};
  }
}

/**
 * Load the language pack with a fallback chain:
 *   1. Decompress from localStorage (matches AngularJS deCompressLanguagePack)
 *   2. If empty, fetch from /API/Script/GetLanguagePack?language=EN and cache it
 *
 * Use this anywhere AngularJS used `deCompressLanguagePack()` as a resolve step.
 */
export async function loadLanguagePack(): Promise<Record<string, string>> {
  const fromStorage = deCompressLanguagePack();
  if (Object.keys(fromStorage).length > 0) {
    return fromStorage;
  }
  return fetchAndStoreLanguagePack();
}

/**
 * Module-level in-memory cache for the language pack.
 *
 * Two-tier caching (restores AngularJS parity — see public/index.js on the
 * `master_7_23_26` branch):
 *   1. This in-memory variable — fast synchronous reads within a page session.
 *      Dies on full page reload.
 *   2. localStorage (GZIPPED via pako) — written by `persistLanguagePack()`.
 *      Survives reloads so `deCompressLanguagePack()` rehydrates synchronously
 *      on the next load WITHOUT a blocking re-fetch.
 *
 * Why localStorage is safe here (an earlier version of this file went in-memory
 * only and explained why NOT to use localStorage — that reasoning was specific
 * to storing PLAIN JSON):
 *   - Plain JSON is ~121k entries / ~6 MB, which DOES exceed the ~5 MB quota and
 *     throws QuotaExceededError. So we never store plain JSON.
 *   - GZIPPED, the pack is ~1.5 MB (~3 MB in UTF-16 localStorage accounting) —
 *     comfortably under quota. This is exactly what the AngularJS shell stored in
 *     production (`compressLanguagePack()`), so it is a proven format.
 *   - The prior "pako is a legacy global we shouldn't couple to" concern is
 *     resolved by importing `pako` as a real npm dependency (bundled/versioned),
 *     which is independent of the AngularJS `window.pako` global.
 *   - Trade-off: gzip on write / ungzip+parse on read cost CPU on the main
 *     thread (same cost the old app paid on bootstrap), but this REPLACES a
 *     ~1.5 MB network fetch on every reload and is synchronous, so it removes
 *     the blocking bootstrap spinner.
 *
 * Ownership: populated by `StandaloneBootstrap` (standalone mode) or by
 * `loadLanguagePack()` on first call (any mode). Consumed synchronously by
 * `deCompressLanguagePack()` and `translate()` inside every CoreScript route.
 */
let inMemoryLanguagePack: Record<string, string> | null = null;

/**
 * Persist the language pack to localStorage as a pako-gzipped binary string.
 *
 * Byte-compatible with AngularJS `compressLanguagePack()` (public/index.js:661-666),
 * which used pako 1.x `gzip(..., { to: 'string' })`. pako 2.x dropped the
 * string-output option for gzip and always returns a Uint8Array, so we convert
 * the bytes to a binary string ourselves (chunked — a naive spread would blow
 * the call stack on a ~1.5 MB buffer). The result is the same binary string that
 * `deCompressLanguagePack()` reconstructs via `Uint8Array.from(stored, c => c.charCodeAt(0))`.
 *
 * Best-effort: quota / private-mode failures are swallowed (the in-memory cache
 * still serves the current session), matching the codebase's other storage
 * writes (e.g. authStore, ResultsRoute).
 */
function bytesToBinaryString(bytes: Uint8Array): string {
  let out = '';
  const CHUNK = 0x8000; // 32k — keeps String.fromCharCode args under engine limits
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return out;
}

function persistLanguagePack(translations: Record<string, string>): void {
  try {
    const compressed = bytesToBinaryString(gzip(JSON.stringify(translations)));
    localStorage.setItem('languagePack', compressed);
  } catch {
    // localStorage quota / private mode — non-fatal; in-memory cache still works.
  }
}

/**
 * Populate the in-memory language pack AND persist it (gzipped) to localStorage.
 * Called by `StandaloneBootstrap` once the API fetch resolves, BEFORE any
 * CoreScript route is allowed to mount, and by `fetchAndStoreLanguagePack()`.
 */
export function setLanguagePack(translations: Record<string, string>): void {
  inMemoryLanguagePack = translations;
  persistLanguagePack(translations);
}

/**
 * Synchronous read for the language pack.
 *
 * Resolution order (any miss falls through to the next):
 *   1. In-memory cache — populated by `setLanguagePack()`. Primary path once the
 *      pack has loaded during the current page session.
 *   2. localStorage plain JSON — leading `{` character. Backward-compat with
 *      any prior standalone session that wrote plain JSON.
 *   3. localStorage pako-gzipped binary string — written by `persistLanguagePack()`
 *      (standalone) or the AngularJS shell's compressLanguagePack() (embedded).
 *      Decompressed with the bundled `ungzip` (or `window.pako.ungzip` if the
 *      AngularJS global is present). This is what lets a full page reload
 *      rehydrate the pack synchronously without a blocking re-fetch.
 *
 * Returns `{}` when nothing is available — caller is expected to fall through
 * to `loadLanguagePack()` (or `StandaloneBootstrap`'s cold path) which fetches
 * from the API and populates both cache tiers.
 *
 * Stays synchronous because all ~30 CoreScript routes call this inside their
 * `Promise.all` resolve step.
 */
export function deCompressLanguagePack(): Record<string, string> {
  // 1. In-memory cache (standalone primary path).
  if (inMemoryLanguagePack !== null) {
    return inMemoryLanguagePack;
  }

  // 2 & 3. localStorage fallback (embedded mode + backward compat).
  const stored = localStorage.getItem('languagePack');
  if (!stored) {
    return {};
  }

  // Plain JSON — first byte '{' (0x7b). Gzipped binary strings begin with
  // 0x1f, so the disambiguation is unambiguous.
  if (stored.charCodeAt(0) === 0x7b /* '{' */) {
    try {
      const parsed = JSON.parse(stored);
      inMemoryLanguagePack = parsed; // promote to in-memory for subsequent reads
      return parsed;
    } catch (err) {
      console.error('[deCompressLanguagePack] failed to parse JSON', err);
      return {};
    }
  }

  // Pako-gzipped binary string — written by `persistLanguagePack()` (standalone)
  // or by the AngularJS shell's compressLanguagePack() (embedded). pako 2.x
  // requires Uint8Array input even when {to:'string'} is the OUTPUT format.
  const bytes = Uint8Array.from(stored, (c) => c.charCodeAt(0));

  try {
    // Prefer the bundled `ungzip`; fall back to the AngularJS `window.pako`
    // global if it is present (embedded mode) and somehow preferred.
    const decompressed = window.pako
      ? window.pako.ungzip(bytes, { to: 'string' })
      : ungzip(bytes, { to: 'string' });
    const parsed = JSON.parse(decompressed);
    inMemoryLanguagePack = parsed; // promote to in-memory for subsequent reads
    return parsed;
  } catch (err) {
    // Corrupt or incompatible entry (e.g. an orphan left by a previous
    // deployment) — drop it so it stops triggering this path, and fall back to
    // an API re-fetch via the caller / bootstrap.
    console.error('[deCompressLanguagePack] failed to decompress; dropping stale entry', err);
    try {
      localStorage.removeItem('languagePack');
    } catch {
      // ignore
    }
    return {};
  }
}

// ============ ORDER NUMBER ============

/**
 * Get next order number for OrderEntry.
 * AngularJS: generalDataService.getNextOrderNumber(propertyLoanId)
 * Endpoint: GET spiglassapi/API/Submission/GetNextOrderNumberV2?propertyLoanId=...
 * Returns: response.data (array with [{key: "orderNumber"}])
 */
export async function getNextOrderNumber(
  propertyLoanId: string | number
): Promise<{ key: string }[]> {
  const response = await axiosClient.get(
    `${SPIGLASSAPI_BASE}/API/Submission/GetNextOrderNumberV2`,
    { params: { propertyLoanId } }
  );
  return response.data;
}

// ============ WORKORDER REPLACEMENT ============

/**
 * Replace global workorder when order number mismatches.
 * AngularJS: propertySelectedService.getAndReplaceGlobalWorkorder(orderNumber)
 *   → calls generalDataService.getWorkOrderInfo(orderNumber)
 *   → on success: storeOrder(data.data, true) + $rootScope.$broadcast('spiSearchCallBack')
 *
 * Endpoint: GET spiglassapi/API/WorkOrderInfo/?orderNumber=...&includeWorkflowStateTransition=true
 *
 * This is a fire-and-forget call — the caller does NOT await the result.
 * The side effect updates $rootScope.G_WORKORDER via the AngularJS propertySelectedService.
 */
export async function getAndReplaceGlobalWorkorder(orderNumber: string): Promise<void> {
  const response = await axiosClient.get(`${SPIGLASSAPI_BASE}/API/WorkOrderInfo/`, {
    params: { orderNumber, includeWorkflowStateTransition: true },
  });

  if (response.data && response.data.id > 0) {
    // Write to the React store (dual-writes to $rootScope.G_WORKORDER in embedded mode).
    const { usePropertyStore } = await import('@/store/propertyStore');
    usePropertyStore.getState().setWorkOrder(response.data);

    // Mirror AngularJS storeOrder() — push into localStorage.recentOrders so the
    // SearchWidget's "Recently Viewed" list reflects the order on next open.
    const { storeRecentOrder } = await import('@/features/search/useRecentOrders');
    storeRecentOrder(response.data);

    // Notify listeners — AngularJS broadcast (embedded) + React window CustomEvent.
    const { broadcastAngularEvent } = await import('@/lib/broadcastAngularEvent');
    broadcastAngularEvent('spiSearchCallBack');
    broadcastAngularEvent('LOAD_RECENT_ORDERS');
    window.dispatchEvent(new CustomEvent('audit-spi-search-callback'));
  }
}

// ============ DYNAMIC ANSWERS ============

/**
 * Fetch dynamic dropdown options.
 * AngularJS: generalDataService.makeGenericAPICall(endpoint, params)
 * Endpoint: GET spiglassapi{endpoint}?{params}
 */
export async function makeGenericAPICall(
  endpoint: string,
  params: Record<string, string>
): Promise<unknown[]> {
  const response = await axiosClient.get(`${SPIGLASSAPI_BASE}${endpoint}`, { params });
  return response.data;
}

// ============ FORM SUBMISSION ============

/**
 * Submit the form transport object.
 * AngularJS: coreScriptService.constructAndSendRequest() calls $http.put(url, transport)
 */
export async function submitTransport(url: string, transport: TransportObject): Promise<unknown> {
  const response = await axiosClient.put(url, transport);
  return response.data;
}

/**
 * Get the submission URL based on request type.
 * Matches AngularJS coreScriptService.constructAndSendRequest() URL selection logic.
 */
export function getSubmitUrl(requestType: string, hasCorrelationId: boolean): string {
  if (requestType === REQUEST_TYPE.ORDER_TYPE && hasCorrelationId) {
    return `${SPIGLASSAPI_BASE}/API/Submission/AuditOrderRequest/`;
  }
  if (requestType === REQUEST_TYPE.SCHEDULE_TYPE) {
    return `${SPIGLASSAPI_BASE}/API/ILSchedule/CreateILOrderSchedule`;
  }
  if (requestType === REQUEST_TYPE.AUDIT_REQUEST_TYPE) {
    return `${SPIGLASSAPI_BASE}/API/Submission/ReceiveOrder/`;
  }
  if (requestType === REQUEST_TYPE.POST_AUDIT_REQUEST_TYPE) {
    return `${SPIGLASSAPI_BASE}/API/Submission/ProcessPostAuditCorrection/`;
  }
  if (requestType === REQUEST_TYPE.ORDER_TYPE) {
    return `${SPIGLASSAPI_BASE}/API/Submission/CreateAuditSubmission/`;
  }
  if (requestType === REQUEST_TYPE.VENDOR_TEXT_TYPE) {
    return `${VMAPI_BASE}/API/VendorManagement/SaveVendorText/`;
  }

  // Default for: PropertyRequest, TextRequest, VendorReassign, ICCStatus,
  // ICCConfirmation, OrderModification, ReconveyRequest, ConveyImpediments, CodeViolation
  return `${SPIGLASSAPI_BASE}/API/Submission/CreateResultTransportRequest/`;
}
